import type {
  MeasurementAtlasSnapshot,
  MeasurementCampaignSnapshot,
} from '@/api/measurementsSnapshot';
import type { PropertyForestContext } from '@/api/propertyForestContext';
import type {
  FmMeasurementPlotMappingStatus,
  FmMeasurementPlotOverlayFeature,
  FmPrimaryMapWorkspaceData,
  VisualizationGeoJson,
} from '@/shared/config/mapSlotContracts';

type AtlasMapContext = MeasurementAtlasSnapshot['map_contexts'][number];
type AtlasMapStand = AtlasMapContext['stands'][number];

function atlasStandFeature(
  context: AtlasMapContext,
  stand: AtlasMapStand,
) {
  return {
    type: 'Feature' as const,
    id: `property:${context.property_id}:stand:${stand.stand_id}`,
    geometry: stand.geometry,
    properties: {
      stand_id: stand.stand_id,
      standId: stand.stand_id,
      stand_number: stand.stand_number,
      STANDNUMBER: stand.stand_number,
      property_id: context.property_id,
      species_code: stand.dominant_species_code,
    },
  };
}

function atlasVisualizationData(
  contexts: readonly AtlasMapContext[],
  propertyId: string | null,
  inventoryRevision: string,
): VisualizationGeoJson | null {
  return contexts.length
    ? {
        type: 'FeatureCollection',
        _propertyId: propertyId,
        _inventoryRevision: inventoryRevision,
        features: contexts.flatMap((context) => (
          context.stands.map((stand) => atlasStandFeature(context, stand))
        )),
      }
    : null;
}

export function atlasMappingStatus(
  value: MeasurementAtlasSnapshot['plots'][number]['mapping_status'],
  method: string | undefined,
): FmMeasurementPlotMappingStatus {
  if (value === 'unmapped') return 'unmapped';
  if (value === 'needs_review') return 'auto_review';
  return method === 'manual' ? 'manual' : 'auto';
}

export function atlasPlotFeature(
  plot: MeasurementAtlasSnapshot['plots'][number],
  campaign: Pick<
    MeasurementAtlasSnapshot['campaigns'][number],
    'id' | 'name' | 'property_id' | 'measurement_date' | 'status' | 'tool' | 'tool_version'
  >,
): FmMeasurementPlotOverlayFeature {
  return {
    id: plot.id,
    sample_id_external: plot.sample_id_external,
    name: plot.name,
    area_m2: plot.area_m2,
    plot_shape: plot.plot_shape,
    measure_method_type: plot.measure_method_type,
    geometry_is_inferred: plot.geometry.inferred,
    tree_count: plot.tree_count,
    measurement_method: plot.measurement_method,
    radius_m: plot.geometry.radius_m,
    scan_width_m: plot.geometry.scan_width_m,
    scan_length_m: plot.geometry.scan_length_m,
    width_m: plot.geometry.width_m,
    length_m: plot.geometry.length_m,
    center_latitude: plot.geometry.center_latitude,
    center_longitude: plot.geometry.center_longitude,
    bearing_deg: plot.geometry.bearing_deg,
    campaignLabel: campaign.name,
    measurementDate: campaign.measurement_date,
  };
}

function campaignPlotFeature(
  plot: MeasurementCampaignSnapshot['plots'][number],
  campaign: MeasurementCampaignSnapshot['campaign'],
): FmMeasurementPlotOverlayFeature {
  return {
    id: plot.id,
    sample_id_external: plot.sample_id_external,
    name: plot.name,
    area_m2: plot.area_m2,
    plot_shape: plot.plot_shape,
    measure_method_type: plot.measure_method_type,
    geometry_is_inferred: plot.geometry.inferred,
    tree_count: plot.trees.length,
    measurement_method: plot.measurement_method,
    radius_m: plot.geometry.radius_m,
    scan_width_m: plot.geometry.scan_width_m,
    scan_length_m: plot.geometry.scan_length_m,
    width_m: plot.geometry.width_m,
    length_m: plot.geometry.length_m,
    center_latitude: plot.geometry.center_latitude,
    center_longitude: plot.geometry.center_longitude,
    bearing_deg: plot.geometry.bearing_deg,
    campaignLabel: campaign.name,
    measurementDate: campaign.measurement_date,
  };
}

function visualizationData(
  context: PropertyForestContext | null,
  propertyId: string,
): VisualizationGeoJson | null {
  if (!context?.standsGeoJson) return null;
  return {
    ...context.standsGeoJson,
    _propertyId: propertyId,
    _inventoryRevision: context.visualization.inventory_revision,
  };
}

/** Shared property and stand context for governed measurement map slots. */
export function buildMeasurementPropertyMapData(
  propertyId: string,
  context: PropertyForestContext | null,
  contextLoading: boolean,
): FmPrimaryMapWorkspaceData {
  return {
    activePropertyId: propertyId,
    propertyForestContext: context,
    propertyForestContextLoading: contextLoading,
    riskOverlay: null,
    riskInventoryRevision: null,
    restoredSelection: null,
    restoredViewport: null,
    visualizationData: visualizationData(context, propertyId),
    measurementPlotOverlay: null,
    onViewportChange: () => undefined,
  };
}

/** One atlas property's authoritative stand geometry without another request. */
export function buildMeasurementAtlasPropertyMapData(
  snapshot: MeasurementAtlasSnapshot | undefined,
  propertyId: string,
  loading: boolean,
): FmPrimaryMapWorkspaceData {
  const context = snapshot?.map_contexts.find(
    (candidate) => candidate.property_id === propertyId
      && candidate.status !== 'unavailable',
  );
  return {
    activePropertyId: propertyId,
    propertyForestContext: null,
    propertyForestContextLoading: loading,
    riskOverlay: null,
    riskInventoryRevision: null,
    restoredSelection: null,
    restoredViewport: null,
    visualizationData: context
      ? atlasVisualizationData([context], propertyId, context.inventory_revision)
      : null,
    measurementPlotOverlay: null,
    onViewportChange: () => undefined,
  };
}

/** Shared renderer data for a real multi-property measurement atlas. */
export function buildMeasurementAtlasMapData(
  snapshot: MeasurementAtlasSnapshot | null,
  visibleCampaignIds: ReadonlySet<number>,
  visiblePropertyIds: ReadonlySet<string>,
): FmPrimaryMapWorkspaceData | null {
  if (!snapshot) return null;
  const campaigns = new Map(
    snapshot.campaigns.map((campaign) => [campaign.id, campaign]),
  );
  const visiblePlots = snapshot.plots.filter(
    (plot) => visibleCampaignIds.has(plot.campaign_id)
      && visiblePropertyIds.has(plot.property_id),
  );
  const contexts = snapshot.map_contexts.filter(
    (context) => context.status !== 'unavailable'
      && visiblePropertyIds.has(context.property_id),
  );
  const activePropertyId = visiblePropertyIds.size === 1
    ? [...visiblePropertyIds][0] ?? null
    : null;
  const mapFeatures = atlasVisualizationData(
    contexts,
    activePropertyId,
    snapshot.snapshot_revision,
  );
  const plots = visiblePlots.flatMap((plot) => {
    const campaign = campaigns.get(plot.campaign_id);
    return campaign ? [atlasPlotFeature(plot, campaign)] : [];
  });
  return {
    activePropertyId,
    propertyForestContext: null,
    propertyForestContextLoading: false,
    riskOverlay: null,
    riskInventoryRevision: null,
    restoredSelection: null,
    restoredViewport: null,
    visualizationData: mapFeatures,
    measurementPlotOverlay: {
      plots,
      mappingStatuses: Object.fromEntries(
        visiblePlots.map((plot) => [
          String(plot.id),
          atlasMappingStatus(
            plot.mapping_status,
            plot.primary_mapping?.mapping_method,
          ),
        ]),
      ),
    },
    onViewportChange: () => undefined,
  };
}

/** One campaign's served forest geometry and measured plot footprints. */
export function buildMeasurementCampaignMapData(
  snapshot: MeasurementCampaignSnapshot,
): FmPrimaryMapWorkspaceData {
  const context = snapshot.map_context;
  return {
    activePropertyId: context.property_id,
    propertyForestContext: null,
    propertyForestContextLoading: false,
    riskOverlay: null,
    riskInventoryRevision: null,
    restoredSelection: null,
    restoredViewport: null,
    visualizationData: atlasVisualizationData(
      [context],
      context.property_id,
      context.inventory_revision,
    ),
    measurementPlotOverlay: {
      plots: snapshot.plots.map((plot) => (
        campaignPlotFeature(plot, snapshot.campaign)
      )),
      mappingStatuses: Object.fromEntries(
        snapshot.plots.map((plot) => [
          String(plot.id),
          atlasMappingStatus(
            plot.mapping_status,
            plot.primary_mapping?.mapping_method,
          ),
        ]),
      ),
    },
    onViewportChange: () => undefined,
  };
}
